import React from 'react';
import {
    Box, Typography, TextField, Autocomplete, Chip, Button, Link
} from '@mui/material';
import { Person } from '../../types/person';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Download } from 'lucide-react';

interface FeedbackFormProps {
    people: Person[];
    participants: Person[];
    setParticipants: (participants: Person[]) => void;
    summary: string;
    setSummary: (summary: string) => void;
    attachments: File[];
    setAttachments: (files: File[]) => void;
    existingAttachments: any[];
    isViewMode: boolean;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({
    people,
    participants,
    setParticipants,
    summary,
    setSummary,
    attachments,
    setAttachments,
    existingAttachments,
    isViewMode
}) => {
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setAttachments(Array.from(event.target.files));
        }
    };

    return (
        <Box sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            mt: 3,
            backgroundColor: '#f8f9fa' // Um fundo leve para destacar a seção
        }}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 2 }}>
                REGISTRO DE FEEDBACK
            </Typography>

            {/* --- Campo de Participantes --- */}
            <Autocomplete
                multiple
                id="feedback-participants"
                options={people}
                getOptionLabel={(option) => option.name}
                value={participants}
                onChange={(event, newValue) => {
                    setParticipants(newValue);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        label="Participantes do Feedback"
                        placeholder="Selecione os participantes"
                        size="small"
                        margin="dense"
                        InputLabelProps={{ style: { fontSize: '0.85rem' } }}
                        sx={{
                            '& .MuiInputBase-root': { height: 'auto', fontSize: '0.85rem', p: '2.5px' },
                        }}
                    />
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            variant="outlined"
                            label={option.name}
                            size="small"
                            sx={{ fontSize: '0.75rem', height: '22px' }}
                            {...getTagProps({ index })}
                        />
                    ))
                }
                readOnly={isViewMode}
                sx={{
                    mb: 2,
                    '& .MuiInputBase-root': { flexWrap: 'wrap', height: 'auto', backgroundColor: isViewMode ? '#f0f0f0' : 'white' }
                }}
            />

            {/* --- Campo de Pauta/Resumo com Editor Rico --- */}
            <Box>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
                    Pauta / Resumo do Feedback *
                </Typography>
                <ReactQuill
                    theme="snow"
                    value={summary}
                    onChange={setSummary}
                    style={{ backgroundColor: isViewMode ? '#f0f0f0' : 'white' }}
                    readOnly={isViewMode}
                    modules={{
                        toolbar: isViewMode ? false : [
                            [{ 'header': [1, 2, false] }],
                            ['bold', 'italic', 'underline'],
                            [{'list': 'ordered'}, {'list': 'bullet'}]
                        ],
                    }}
                />
            </Box>

            <style>
                {`
                    .ql-editor {
                        min-height: 150px;
                    }
                    .ql-toolbar.ql-snow {
                        display: ${isViewMode ? 'none' : 'block'};
                    }
                `}
            </style>

            {/* --- Campo de Anexo de Arquivos --- */}
            <Box mt={2}>
                {!isViewMode ? (
                    <>
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{
                                px: 2,
                                py: 0.5,
                                height: 36,
                                fontSize: '0.75rem',
                                borderRadius: 1,
                                textTransform: 'uppercase'
                            }}
                        >
                            Anexar Arquivos
                            <input
                                type="file"
                                hidden
                                multiple
                                onChange={handleFileChange}
                            />
                        </Button>
                        {attachments.length > 0 && (
                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                {attachments.length} arquivo(s) selecionado(s).
                            </Typography>
                        )}
                    </>
                ) : (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>Anexos:</Typography>
                        {existingAttachments.length > 0 ? (
                            existingAttachments.map(file => (
                                <Link
                                    key={file.id}
                                    href={file.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center', mt: 1, textDecoration: 'none' }}
                                >
                                    <Download size={16} style={{ marginRight: 8 }} />
                                    {file.file_name}
                                </Link>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Nenhum anexo.</Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FeedbackForm;
